import type { Messages } from '../catalogue.en'

/**
 * The Person, in Hebrew.
 *
 * This module is why the surface exists. The same field was translated three
 * times under three ids, so the workspace printed 'דוא"ל' on a chart and
 * 'אימייל' on the account card for one Person's one email address. One id, one
 * translation, one word on every screen.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports this
 * module directly: consumers ask the catalogue, so how the messages are stored
 * stays this layer's business.
 */
export const persons: Partial<Messages> = {
  'person.identity': 'פרטים אישיים',
  'person.contact': 'פרטי קשר',

  'person.fullName': 'שם מלא',
  'person.phone': 'טלפון',
  'person.email': 'דוא"ל',

  'person.notRecorded': 'לא תועד',

  'person.edit': 'עריכת פרטים',
  'person.save': 'שמור פרטים',
  'person.saving': 'שומר…',
  'person.cancel': 'ביטול',
  'person.lastUpdated': 'עודכן לאחרונה',

  'person.discardTitle': 'שינויים שלא נשמרו',
  'person.discardDetail': 'הפעולה תבטל את השינויים שביצעת כאן. מה שכבר נשמר אינו מושפע.',
  'person.discardChanges': 'בטל שינויים',
  'person.keepEditing': 'המשך לערוך',

  'person.staleRevision':
    'הפרטים שונו במקום אחר בזמן העריכה. הטקסט שלך נשמר; טען את הגרסה השמורה כדי לראות את הפרטים הנוכחיים לפני השמירה.',
  'person.reloadSaved': 'טען את הגרסה השמורה',
  'person.reloadFailed': 'לא ניתן היה לטעון את הגרסה השמורה. השינויים שלך עדיין כאן; נסה שוב.',
  'person.saveRejected': 'הפרטים נדחו כפי שהוזנו. בדוק את השדות ונסה שוב.',
  'person.saveFailed': 'לא ניתן היה לשמור את הפרטים. נסה שוב.',

  'person.names.title': 'שמות',
  'person.names.edit': 'עריכת שמות',
  'person.names.saveNames': 'שמור שמות',
  'person.names.loading': 'טוען שמות…',
  'person.names.loadFailed': 'לא ניתן היה לטעון את השמות.',
  'person.names.notFound': 'לא תועדו שמות לאדם זה.',
  'person.names.unsavedIntent': 'שינויים בשמות',

  'person.names.kind.family': 'שם משפחה',
  'person.names.kind.given': 'שם פרטי',
  'person.names.kind.patronymic': 'שם האב',
  'person.names.kind.prefix': 'תואר לפני השם',
  'person.names.kind.suffix': 'תוספת אחרי השם',

  'person.names.use': 'שימוש',
  'person.names.use.unknown': 'לא צוין',
  'person.names.use.usual': 'רגיל',
  'person.names.use.official': 'רשמי',
  'person.names.use.temp': 'זמני',
  'person.names.use.nickname': 'כינוי',
  'person.names.use.anonymous': 'אנונימי',
  'person.names.use.old': 'שם קודם',
  'person.names.use.maiden': 'שם נעורים',

  'person.names.source': 'מקור',
  'person.names.source.reported': 'כפי שנמסר',
  'person.names.source.document': 'ממסמך',
  'person.names.source.passport': 'מדרכון',
  'person.names.source.migration': 'רשומה שהועברה',
  'person.names.sourceReference': 'מספר המסמך',

  'person.names.order': 'סדר',
  'person.names.order.givenFamily': 'שם פרטי תחילה',
  'person.names.order.familyGiven': 'שם משפחה תחילה',
  'person.names.shortStyle': 'צורה מקוצרת',
  'person.names.shortStyle.full': 'כמו השם המלא',
  'person.names.shortStyle.initials': 'ראשי תיבות עם שם משפחה',
  'person.names.shortStyle.firstGiven': 'שם פרטי ראשון עם שם משפחה',

  'person.names.language': 'שפת השם',
  'person.names.language.unknown': 'לא צוינה',
  'person.names.international': 'כתיב בין־לאומי',
  'person.names.preferred': 'מועדף',
  'person.names.periodStart': 'בשימוש מ־',
  'person.names.periodEnd': 'בשימוש עד',

  'person.names.primaryMark': 'מוצג',
  'person.names.preferredMark': 'מועדף',
  'person.names.internationalMark': 'בין־לאומי',
  'person.names.enteredInErrorMark': 'הוזן בטעות',
  'person.names.periodFrom': 'מ־{start}',
  'person.names.periodUntil': 'עד {end}',
  'person.names.periodBetween': '{start} עד {end}',

  'person.names.nth': 'שם {n}',
  'person.names.parts': 'חלקי השם',
  'person.names.partKind': 'סוג החלק {n}',
  'person.names.addPart': 'הוסף חלק',
  'person.names.movePartUp': 'העבר את חלק {n} למעלה',
  'person.names.movePartDown': 'העבר את חלק {n} למטה',
  'person.names.removePart': 'הסר את חלק {n}',
  'person.names.addName': 'הוסף שם',
  'person.names.remove': 'הסר שם זה',
  'person.names.endUse': 'סיום שימוש',
  'person.names.enteredInError': 'הוזן בטעות',
  'person.names.previewFull': 'מלא:',
  'person.names.previewShort': 'מקוצר:',
  'person.names.previewPending': 'התצוגה המקדימה תופיע תוך כדי הקלדה.',
  'person.names.shownAs': 'יוצג כ־',
  'person.names.shownWith': 'לצד',

  'person.names.problem.none': 'נדרש לפחות שם אחד.',
  'person.names.problem.blankPart': 'לכל חלק בשם נדרש ערך. הסר חלק במקום להשאירו ריק.',
  'person.names.problem.familyTwice': 'שם יכול לשאת שם משפחה אחד בלבד.',
  'person.names.problem.periodOrder': 'היום האחרון בשימוש אינו יכול להקדים את הראשון.',
  'person.names.problem.migrationSource': 'שם זה הועבר ממערכת קודמת. לאחר שינוי יש לציין מהיכן הכתיב.',

  'person.names.saveFailed': 'לא ניתן היה לשמור את השמות. נסה שוב.',
  'person.names.invalid': 'השמות נדחו כפי שהוזנו. בדוק את השדות ונסה שוב.',
  'person.names.ambiguous': 'שניים מהשמות יוצגו באותו מקום. סמן אחד כמועדף או סיים את השימוש באחד.',
  'person.names.inactive': 'רשומה זו אינה פעילה, ולכן לא ניתן לשנות את שמותיה.',
  'person.names.staleRevision': 'השמות שונו במקום אחר בזמן העריכה. הטקסט שלך נשמר; טען את הגרסה השמורה לפני השמירה.',

  'person.names.reasonLegend': 'סיבה',
  'person.names.reasonOtherDetail': 'פרט מדוע',
  'person.names.reasonHint': 'בחר את הסיבה הקרובה ביותר או כתוב סיבה.',
  'person.names.reasonOwed': 'כדי לשמור יש לציין מדוע השמות משתנים.',
  'person.names.reason.correction': 'תיקון טעות',
  'person.names.reason.nameChange': 'השם השתנה',
  'person.names.reason.documentUpdate': 'מסמך עודכן',
  'person.names.reason.preferenceChange': 'ההעדפה השתנתה',
  'person.names.reason.endUse': 'שם אינו בשימוש עוד',
  'person.names.reason.enteredInError': 'שם הוזן בטעות',
  'person.names.reason.other': 'אחר',
  'person.names.event.registration': 'נרשם',
  'person.names.event.migration': 'הועבר',

  'person.history': 'היסטוריה',
  'person.names.historyLabel': 'היסטוריה: שמות',
  'person.names.historyLoading': 'טוען היסטוריה…',
  'person.names.historyFailed': 'לא ניתן היה לטעון את ההיסטוריה.',
  'person.names.historyEmpty': 'אין גרסאות קודמות.',
  'person.names.historyMore': 'גרסאות קודמות',
  'person.names.recordedAt': 'תועד',
  'person.names.actorSystem': 'מערכת',
  'person.change': 'שינוי',
  'person.noChanges': 'עדיין לא בוצעו שינויים.',
  'person.names.formerly': 'לשעבר',
  'person.names.group.print': 'כיצד השם מודפס',
  'person.names.group.meaning': 'למה משמש השם',
  'person.names.group.sourcePeriod': 'מקור ותקופה',
  "person.names.advanced": "אפשרויות נוספות",
  "person.names.optionalReason": "סיבת השינוי",
  "person.names.optional": "לא חובה",
  "person.names.addPatronymic": "הוספת שם אב",
  "person.names.addMiddle": "הוספת שם אמצעי",
  "person.names.national": "כתיב בשפה לאומית",
  "person.names.addOther": "גרסה אחרת (אפשרויות נוספות)…",
  "person.names.keepOne": "לא ניתן להסיר את הכתיב האחרון; אפשר לתקן או להחליף אותו.",
  "person.names.problem.internationalScript": "יש להשתמש באותיות לטיניות בכתיב הבינלאומי.",
  'person.names.updated': 'השמות עודכנו',
  "person.names.noReason": "ללא ציון סיבה",
}
